import { useContext, useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { classNames } from 'primereact/utils'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DateTime } from 'luxon';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import _ from "lodash"

// Custom Components
import { UserContext } from "../../context/userContext"
import AppointmentsFormCron from './AppointmentsFormCron';
import { LoaderMedium } from '../Loaders';

// Utils
import { durations, engagementChannels, getDateKey, reformatFormikPayload, requestModalModeOptions } from './utils';
import { LogError } from '../../helpers/logger';

// Styles
import myStyles from "../../styles/appointments/Appointments.module.css"
import { parseCronExpression } from 'cron-schedule';

export default function AppointmentsRequestDialog({
  showRequestAppt,
  setShowRequestAppt,
  requestModalMode,
  appointmentToEdit, // Appointment object to edit, if any.
  setAppointmentToEdit, // Function to set the appointment object.
  fetchStaffAvailabilities,
  services,
  submitAppointmentPayload
}) {
  const userCtx = useContext(UserContext)
  const [loading, setLoading] = useState(false)
  const [initPayload, setInitPayload] = useState(null) // Initial payload for the form.
  const [rawStaffAvailability, setRawStaffAvailability] = useState([]) // Staff availabilities for the selected staff.
  const [processedStaffAvailability, setProcessedStaffAvailability] = useState([]) // Staff availabilities for the selected staff.
  const [timeSelectType, setTimeSelectType] = useState("calendar") // Type of time selection input ("calendar" or "dropdown").

  const formik = useFormik({
    initialValues: {
      id: 0,
      organization: null,
      staff: null,
      isRecurring: false,
      startDate: null,
      endDate: null,
      originalStartDateTime: null,
      originalEndDateTime: null,
      time: null,
      duration: null,
      cron: null, // All appointments have "frequency", and the cronDescription should come from this.
      type: null,
      channel: null,
    },
    validationSchema: Yup.object({
      organization: Yup.string().required("Organization is required"),
      staff: Yup.string().required("Staff is required"),
      startDate: Yup.date().when("isRecurring", {
        is: (isRecurring) => isRecurring === true,
        then: () => Yup.date().required("Start date is required").min(
          DateTime.local().startOf("day").toJSDate(), "Date cannot be in the past"),
        otherwise: () => Yup.date().required("Date is required").min(
          DateTime.local().startOf("day").toJSDate(), "Date cannot be in the past"),
      }),
      endDate: Yup.date()
        .when("isRecurring", {
          is: (isRecurring) => isRecurring === true,
          then: () => Yup.date()
            .required("End date is required")
            .min(DateTime.local().startOf("day").toJSDate(), "End date cannot be in the past")
            .test(
              "is-after-start",
              "End date must be after start date",
              function (endDate) {
                const { startDate } = this.parent;
                if (!startDate || !endDate) return true; // Don't show error if either is missing
                const start = new Date(startDate);
                const end = new Date(endDate);
                if (isNaN(start) || isNaN(end)) return true; // Skip if invalid
                return end > start;
              }
            ),
          otherwise: () => Yup.date().nullable(),
        }),
      time: Yup.string()
        .required("Time is required")
        .test(
          "is-future-time-if-today",
          "Time must be in the future if date is today",
          function (value) {
            const { startDate } = this.parent;

            if (!startDate || !value) return true; // Skip if missing

            const now = DateTime.local();
            const selectedDate = DateTime.fromJSDate(startDate);
            const selectedTime = DateTime.fromJSDate(new Date(value));

            if (!selectedTime.isValid) return true; // Skip if can't parse

            // If startDate is today, check that time is in the future
            if (selectedDate.hasSame(now, "day")) {
              // Combine today's date with selected time
              const selectedDateTime = selectedDate.set({
                hour: selectedTime.hour,
                minute: selectedTime.minute,
                second: 0,
                millisecond: 0,
              });
              return selectedDateTime > now;
            }
            return true;
          }
        ),
      duration: Yup.object().required("Duration is required"),
      channel: Yup.string().required("Channel is required"),
      type: Yup.string().required("Type is required"),
      isRecurring: Yup.boolean(),
      cron: Yup.object().when("isRecurring", {
        is: (isRecurring) => isRecurring === true,
        then: () => Yup.object().required("Cron expression is required for recurring appointments"),
        otherwise: () => Yup.object().nullable(),
      }),
    }),
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      let successfull = await submitAppointmentPayload(values, requestModalMode);
      setSubmitting(false);

      if (successfull) {
        setShowRequestAppt(false) // Hide dialog on close.
        setAppointmentToEdit(null) // Reset appointment being edited.
        formik.resetForm(); // Reset form values when dialog is closed.
      }
    },
  });

  useEffect(() => {
    if (showRequestAppt && appointmentToEdit) {
      const editValues = reformatFormikPayload(appointmentToEdit, userCtx)
      formik.setValues(editValues)
      setInitPayload(editValues) // Set initial payload for comparison.
    }
    else if (showRequestAppt && !appointmentToEdit) {
      setInitPayload(formik.values) // Set initial payload to current form values.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRequestAppt])

  // If timeSelectType is changed, convert time values accordingly.
  useEffect(() => {
    if (timeSelectType && formik?.values?.time && timeSelectType === "calendar") {
      // Convert from dropdown values to datetime object
      if (formik.values.time && typeof formik.values.time === "string") {
        const time = DateTime.fromFormat(formik.values.time, "hh:mm a");
        if (time.isValid) {
          const date = DateTime.fromJSDate(formik.values.startDate);
          formik.setFieldValue("time", date.set({ hour: time.hour, minute: time.minute }).toJSDate());
        }
      }
    } else if (timeSelectType && formik?.values?.time && timeSelectType === "dropdown") {
      // Convert from datetime object to dropdown value (closest availableTime match)
      if (formik.values.time && formik.values.time instanceof Date) {
        const date = DateTime.fromJSDate(formik.values.time);
        const startDate = DateTime.fromJSDate(formik.values.startDate);
        const availableTimes = getAvailableTimes();

        const availableDateTimes = availableTimes.map(timeStr =>
          DateTime.fromFormat(
            startDate.toFormat("MM/dd/yyyy") + " " + timeStr,
            "MM/dd/yyyy hh:mm a"
          )
        );

        const dateTime = startDate.set({
          hour: date.hour,
          minute: date.minute,
          second: 0,
          millisecond: 0,
        });

        const closestIndex = availableDateTimes.reduce((closestIdx, currDT, idx) => {
          return Math.abs(dateTime.diff(currDT).as('minutes')) <
            Math.abs(dateTime.diff(availableDateTimes[closestIdx]).as('minutes'))
            ? idx
            : closestIdx;
        }, 0);

        const closestTime = availableTimes[closestIndex];
        formik.setFieldValue("time", closestTime); // Set the closest time as the selected value.
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeSelectType])

  // If processedStaffAvailability has changed, and timeSelectType is "dropdown", update time from (new) available times.
  useEffect(() => {
    if (processedStaffAvailability?.length > 0 && timeSelectType === "dropdown" && formik.values.time && typeof formik.values.time === "string") {
      // Duration has changed and new availableTimes have been generated - set time to the closest matching available time.
      const date = DateTime.fromJSDate(formik.values.startDate);
      const availableTimes = getAvailableTimes();

      // Parse the selected time string as a time on the selected date
      const selectedTime = DateTime.fromFormat(
        date.toFormat("MM/dd/yyyy") + " " + formik.values.time,
        "MM/dd/yyyy hh:mm a"
      );

      // Pre-parse all available times to DateTime objects
      const availableDateTimes = availableTimes.map(timeStr =>
        DateTime.fromFormat(
          date.toFormat("MM/dd/yyyy") + " " + timeStr,
          "MM/dd/yyyy hh:mm a"
        )
      );

      // Find the index of the closest time
      const closestIdx = availableDateTimes.findIndex(dt => dt >= selectedTime);
      const finalIdx = closestIdx === -1 ? availableDateTimes.length - 1 : closestIdx;
      const closestTime = availableTimes[finalIdx];

      formik.setFieldValue("time", closestTime); // Set the closest time as the selected value.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedStaffAvailability])

  useEffect(() => {
    if (formik.values.organization) {
      changeSelectedStaff()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.organization]);

  // Initially process staff availability when rawStaffAvailability has changed
  // Subesequent processing will trigger from changes to duration
  useEffect(() => {
    if (rawStaffAvailability?.length > 0 && (!processedStaffAvailability || processedStaffAvailability.length === 0)) {
      processRawStaffAvailability({ value: formik.values.duration || durations[0] }) // Set initial duration if not set.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawStaffAvailability]);

  async function changeSelectedStaff() {
    setLoading(true);
    try {
      // Get selected organization and its first staff assignment (if any).
      const selectedOrg = userCtx.participant_organizations.find(org =>
        org.organization_id === formik.values.organization)
      const selectedStaff = selectedOrg?.staff_assignment_list[0]?.staff_id || null

      // Update formik values for staff.
      formik.setFieldValue("staff", selectedStaff);

      // Fetch and set availabilities for the selected staff.
      const staffAvailabilities = await fetchStaffAvailabilities(
        userCtx.staff_assignment_list?.find(s => s.staff_id === selectedStaff)
      );
      setRawStaffAvailability(staffAvailabilities);
    }
    catch (error) {
      LogError("Error changing selected staff", error)
    }
    finally {
      setLoading(false);
    }
  }

  /**
   * Determines if a new appointment request is allowed based on participant status and staff assignment.
   * @returns {{allow: boolean, reason: string}} - An object indicating if allowed and the reason if not.
   */
  const canRequestNew = () => {
    let reason = ""
    const ptpOrg = userCtx.participant_organizations.find(
      o => o.organization_id === formik.values.organization
    )

    // Disable new appointment requests for inactive participants.
    let canRequest = !ptpOrg?.participant_membership_status
      .toLowerCase()
      .includes("inactive")

    // If participant is unassigned (staff_id "-4"), disallow new requests.
    if (canRequest && ptpOrg.staff_assignment_list?.length > 0) {
      canRequest = ptpOrg.staff_assignment_list[0]?.staff_id !== "-4"
      reason = !canRequest
        ? "No staff member has been assigned to this profile"
        : reason // Set reason if unassigned.
    }
    else {
      canRequest = false // Disallow if no staff assignment list or other conditions.
      reason = "Your account is inactive" // Set reason if account is inactive.
    }

    return { allow: canRequest, reason: reason }
  }

  // Lays out recurring availabilities by parsing cron expressions and generating individual time slots
  // based on the recurrence rules and duration.
  function layoutRecurringAvailability(recurrings, duration) {

    let availabilities = []
    for (let availableTime of recurrings) {
      // Determine the appointment buffer time (time between appointments).
      const appointmentBuffer = Number(availableTime.appointmentBuffer) || duration

      // Convert recurring start and end dates to Luxon DateTime objects.
      let recurringStartDate = DateTime.fromJSDate(new Date(availableTime.recurringStartDate))
      let recurringEndDate = DateTime.fromJSDate(new Date(availableTime.recurringEndDate)).endOf('day') // Ensure end date is inclusive of the whole day.
      let cron = parseCronExpression(availableTime.cron) // Parse the cron expression.

      // Get next 30 dates based on the cron expression, filtered by the recurring end date.
      let dates = cron
        .getNextDates(30, recurringStartDate.toJSDate()) // Get next 30 occurrences starting from recurringStartDate.
        .filter((x) => recurringEndDate >= DateTime.fromJSDate(x)) // Filter out dates beyond recurringEndDate.

      // For each occurrence date, generate individual time slots based on the appointmentBuffer.
      for (const date of dates) {
        // Create a DateTime object from the JS Date, but specify that its components belong to the staff's timezone.
        // Then, convert this DateTime object to the user's local timezone.
        const cronDate = DateTime.fromJSDate(date);
        let startDate = DateTime.fromObject(
          {
            year: cronDate.year,
            month: cronDate.month,
            day: cronDate.day,
            hour: cronDate.hour,
            minute: cronDate.minute,
          },
          { zone: availableTime.timezone }
        ).setZone("local");

        // Construct the end date of the available slot for that occurrence.
        let endDate = DateTime.fromFormat(
          cronDate.toFormat("MM/dd/yyyy") + " " + availableTime.recurringEndTime,
          "MM/dd/yyyy hh:mm a",
          { zone: availableTime.timezone }
        ).setZone("local");

        let availableTimes = [startDate] // Start with the first available time.
        let nextDate = startDate.plus({ minutes: appointmentBuffer }) // Calculate the next slot start time.

        while (nextDate <= endDate) { // Generate all slots within the availability window.
          availableTimes.push(nextDate)
          nextDate = nextDate.plus({ minutes: appointmentBuffer }) // Move to the next slot.
        }

        let newEntry = { ...availableTime } // Create a copy of the recurring availability.
        newEntry.times = availableTimes
        newEntry.isRecurring = false
        newEntry.individualStartDate = availableTimes[0].toString()
        newEntry.individualEndDate = availableTimes[availableTimes.length - 1].toString()

        availabilities.push(newEntry) // Add to the list of individual availabilities.
      }
    }

    return availabilities
  }

  // Handles the selection of an appointment duration.
  // Recalculates and updates available times based on the new duration.
  function processRawStaffAvailability(ev) {
    if (ev?.value?.mins) {
      let duration = ev.value.mins // Get the duration in minutes.
      const _rawStaffAvailability = _.cloneDeep(rawStaffAvailability) // Clone raw availabilities to avoid mutating state directly.
      setProcessedStaffAvailability([]) // Clear current processed availabilities.

      // Layout recurring availabilities based on the new duration.
      let recurringAvails = layoutRecurringAvailability(
        _rawStaffAvailability.filter((x) => x.isRecurring), // Only process existing recurring availabilities.
        duration
      )

      // Filter and sort individual availabilities.
      let individualAvails = _rawStaffAvailability
        .filter((x) => !x.isRecurring) // Only consider individual (non-recurring) slots.
        .sort((a, b) => { // Sort by individual start date.
          if (
            DateTime.fromFormat(
              a.individualStartDate,
              getDateKey(a.individualStartDate)
            ) <
            DateTime.fromFormat(
              b.individualStartDate,
              getDateKey(b.individualStartDate)
            )
          ) {
            return -1
          }
          if (
            DateTime.fromFormat(
              a.individualStartDate,
              getDateKey(a.individualStartDate)
            ) >
            DateTime.fromFormat(
              b.individualStartDate,
              getDateKey(b.individualStartDate)
            )
          ) {
            return 1
          }
          return 0 // Dates are equal.
        })

      // Update individual availabilities with calculated time slots.
      for (const availableTime of individualAvails) {
        // Determine the appointment buffer time (time between appointments).
        const appointmentBuffer = Number(availableTime.appointmentBuffer) || duration

        let startDate = DateTime.fromJSDate(new Date(availableTime.individualStartDate))
        let endDate = DateTime.fromJSDate(new Date(availableTime.individualEndDate))

        // If individual time slots haven't been calculated for this availability, do it now.
        if (!availableTime.times) {
          let availableTimes = [startDate] // Start with the first available time.
          let nextDate = startDate.plus({ minutes: appointmentBuffer })

          while (nextDate <= endDate) { // Generate all slots within the availability window.
            availableTimes.push(nextDate)
            nextDate = nextDate.plus({ minutes: appointmentBuffer }) // Move to the next slot.
          }

          availableTime.times = availableTimes
        }
      }

      // Combine existing active availabilities with the newly generated recurring ones.
      let avails = [...individualAvails, ...recurringAvails]
      setProcessedStaffAvailability(avails) // Update active availabilities.
    }
  }

  function getAvailableTimes() {
    if (processedStaffAvailability?.length > 0 && formik.values.startDate) {
      var selectedDate = getAvailabilityForSelectedDate() // Get availabilities for the selected date.
      if (selectedDate) {
        // Map available times to a readable format (e.g., "09:00 AM").
        const result = selectedDate.times.map((x) => x.toFormat("hh:mm a"))
        return result
      }
    }

    return [] // Return empty array if no availabilities are found.
  }

  function toggleTimeSelectType(type) {
    if (type !== timeSelectType) {
      setTimeSelectType(type)
    }
  }

  /**
   * Decides which time selection input to render based on whether activeAvailabilities are available
   * and if the appointment is recurring.
   * @param {boolean} isRecurring - True if the appointment is recurring.
   * @returns {JSX.Element} - The appropriate time selection input (Calendar timeOnly or Dropdown).
   */
  function decideTimeSelection() {
    if (!processedStaffAvailability || processedStaffAvailability.length < 1 || !getAvailabilityForSelectedDate()) {
      // If no availabilities are loaded (staff has no set availability) or none for the selected date.
      // Render a simple time-only calendar for direct time input.
      toggleTimeSelectType("calendar")
      return (
        <div className="w-full">
          <label className={myStyles.formLabel} htmlFor="time">Time</label>
          <span className={myStyles.formFieldRequired}>*</span>
          <Calendar
            id="time"
            onChange={formik.handleChange}
            value={formik.values.time}
            className={classNames(
              myStyles.formField,
              getFieldValidation("time") ? myStyles.formFieldBorderError : myStyles.formFieldBorder,
            )}
            timeOnly // Only show time picker.
            hourFormat="12" // 12-hour format.
            disabled={formik.isSubmitting}
          />
          <ValidationText fieldName="time" />
        </div>
      )
    } else {
      // If availabilities are loaded (staff has set availability) for the selected date.
      // Render a dropdown with available times.
      toggleTimeSelectType("dropdown")
      return (
        <div className="w-full">
          <label className={myStyles.formLabel} htmlFor="time">Time</label>
          <span className={myStyles.formFieldRequired}>*</span>
          <Dropdown
            id="time"
            value={formik.values.time}
            options={getAvailableTimes()} // Options are populated from activeAvailabilities.
            onChange={formik.handleChange}
            className={classNames(
              myStyles.formField,
              getFieldValidation("time") ? myStyles.formFieldBorderError : myStyles.formFieldBorder,
            )}
            disabled={formik.isSubmitting}
          />
          <ValidationText fieldName="time" />
        </div>
      )
    }
  }

  function renderDurationInput() {
    return (
      <div className="w-full">
        <label className={myStyles.formLabel} htmlFor="startTime">Duration</label>
        <span className={myStyles.formFieldRequired}>*</span>
        <Dropdown
          id="duration"
          onChange={(e => {
            formik.handleChange(e) // Handle Formik change event.
            processRawStaffAvailability(e) // Call onDurationSelect to update available times.
          })} // Call onDurationSelect when value changes.
          value={formik.values.duration} // Bind to Formik value.
          options={durations} // Options array from utils.
          optionLabel="name" // Display 'name' property of duration objects.
          className={classNames(
            myStyles.formField,
            getFieldValidation("duration") ? myStyles.formFieldBorderError : myStyles.formFieldBorder,
          )}
          disabled={formik.isSubmitting}
        />
        <ValidationText fieldName="duration" />
      </div>
    )
  }

  function getFieldValidation(fieldName) {
    // Check if the field has been touched and has an error.
    return formik.touched[fieldName] && formik.errors[fieldName]
  }

  const ValidationText = ({ fieldName }) => {
    // Render validation error text for a specific field. 
    return (
      getFieldValidation(fieldName) ? (
        <div className={myStyles.formError}>{formik.errors[fieldName]}</div>
      ) : null
    )
  }

  function compareValues(initValues, currentValues, ignoreKeys) {

    // If initial values or form values are not set, return true (indicating no changes).
    if (!initValues || !currentValues) return true;

    // Compare individual values from initPayload and current form values.
    return Object.keys(initValues).every(key => {

      // Ignore specific keys that should not trigger a change detection.
      if (ignoreKeys.includes(key)) return true;

      if (Array.isArray(initValues[key]) && Array.isArray(currentValues[key])) {
        // Compare arrays by checking if they have the same length and all elements are equal.
        return initValues[key].length === currentValues[key].length &&
          initValues[key].every((val, index) => val === currentValues[key][index]);
      }
      return initValues[key] === currentValues[key];
    });
  }

  function getAvailabilityForSelectedDate(date) {
    return processedStaffAvailability.filter((x) => {
      if (!x.isRecurring) { // Only consider individual (non-recurring) availabilities here.
        return (
          DateTime.fromJSDate(date || formik.values.startDate).toFormat("MM/dd/yyyy") ===
          x.times[0].toFormat("MM/dd/yyyy")
        )
      }
      else return false
    })[0] || null; // Return the first matching availability or null if none found.
  }

  const availableDateStrings = useMemo(() => {
    const dateStrings = new Set();
    processedStaffAvailability.forEach((avail) => {
      if (!avail.isRecurring && avail.times && avail.times.length > 0) {
        dateStrings.add(avail.times[0].toFormat("MM/dd/yyyy"));
      }
    });
    return dateStrings;
  }, [processedStaffAvailability]);

  // Custom date template for the calendar component.
  // Adds a visual indicator (a dot) to dates that have upcoming appointments.
  const calendarDateTemplate = (calDate) => {
    const dateStr = DateTime.fromObject({
      year: calDate.year,
      month: calDate.month + 1, // Luxon months are 1-based
      day: calDate.day,
    }).toFormat("MM/dd/yyyy");

    if (availableDateStrings.has(dateStr)) {
      return (
        <span className={myStyles.calendarDateTemplateAvailability}>
          {calDate.day}
        </span>
      );
    }

    return calDate.day;
  };

  return (
    <Dialog
      visible={showRequestAppt} // This dialog is not currently used, but can be opened for confirmation actions.
      onHide={() => {
        setShowRequestAppt(false) // Hide dialog on close.
        setAppointmentToEdit(null) // Reset appointment being edited.
        formik.resetForm(); // Reset form values when dialog is closed.
      }}
      header={() => (
        <div className="pb-1">
          <h4>
            {requestModalMode} {/* Display modal title based on mode */}
          </h4>
        </div>
      )}
      className={classNames("custom_dialog", myStyles.requestDialog)}
    >
      <form onSubmit={formik.handleSubmit} className={myStyles.formContainer}>

        <div className={myStyles.formRow}>
          <div className="w-full">
            <Dropdown
              id="organization"
              name="organization"
              placeholder="Select organization"
              value={formik.values.organization}
              options={userCtx.participant_organizations}
              optionLabel="organization_name" // Display name with organization
              optionValue="organization_id" // Unique identifier for the staff
              onChange={formik.handleChange}
              className={classNames(
                myStyles.formField,
                myStyles.formFieldBorder
              )}
              disabled={loading || formik.isSubmitting || requestModalMode === requestModalModeOptions.change}
            />
          </div>
        </div>

        {loading && (
          <div className={myStyles.formRow}>
            <div className="w-full">
              <LoaderMedium />
            </div>
          </div>
        )}

        {/* Staff selected */}
        {(!loading && formik.values.staff) && (
          <div className={myStyles.formContainerInner}>
            {/* Not allowed to request new appointments */}
            {!canRequestNew().allow && (
              <div className={myStyles.formRow}>
                <div className="w-full">
                  <label className={myStyles.formLabel} htmlFor="reason">
                    Unfortunately, you are not able to request new appointments.
                  </label>
                  <br />
                  <label className={myStyles.formLabel}>
                    Reason: {canRequestNew().reason}
                  </label>
                </div>
              </div>
            )}

            {/* Allowed to request new appointments */}
            {canRequestNew().allow && (
              <>
                <div className={myStyles.formRow}>
                  <div className="w-full">
                    <label className={myStyles.formLabel} htmlFor="startDate">Staff</label>
                    <InputText
                      value={userCtx.staff_assignment_list?.find(s => s.staff_id === formik.values.staff)?.displayName || "No staff found"}
                      readOnly // Make this field read-only
                      className={classNames(
                        myStyles.formField,
                        myStyles.formFieldBorder,
                        myStyles.formFieldDisabled
                      )}
                      disabled={formik.isSubmitting}
                    />
                  </div>
                </div>

                <hr className={myStyles.sectionDivider} />

                {!formik.values.isRecurring && ( // Render single appointment fields
                  <>
                    <div className={classNames(myStyles.formDescription)}>
                      <p className={myStyles.formLabel}>Note: This section is affected by staff availability.</p>
                      <p className="mt-2 mb-2">
                        Dates with set staff-availability are highlighted in the
                        <span className={myStyles.formLabel}> Date</span> calendar, and the type & value of the
                        <span className={myStyles.formLabel}> Time</span> input is dependant on the selected
                        values for <span className={myStyles.formLabel}> Date</span> and
                        <span className={myStyles.formLabel}> Duration</span>.
                      </p>
                      <p className="mb-2">
                        Please confirm the select
                        <span className={myStyles.formLabel}> Time</span> after changing either
                        <span className={myStyles.formLabel}> Date</span> or
                        <span className={myStyles.formLabel}> Duration</span>.
                      </p>
                    </div>

                    <div className={myStyles.formRow}>
                      <div className="w-full">
                        <label className={myStyles.formLabel} htmlFor="startDate">Date</label>
                        <span className={myStyles.formFieldRequired}>*</span>
                        <Calendar
                          id="startDate"
                          onChange={formik.handleChange} // Handle date selection.
                          value={formik.values.startDate}
                          className={classNames(
                            myStyles.formField,
                            getFieldValidation("startDate") ? myStyles.formFieldBorderError : myStyles.formFieldBorder,
                          )}
                          disabled={formik.isSubmitting}
                          dateTemplate={calendarDateTemplate}
                        />
                        <ValidationText fieldName="startDate" />
                      </div>
                      {decideTimeSelection()}
                    </div>

                    <div className={myStyles.formRow}>
                      {renderDurationInput()}
                      <div className={myStyles.hiddenMobile} />
                    </div>
                  </>
                )}

                {formik.values.isRecurring && ( // Render recurring appointment fields
                  <>
                    <div className={myStyles.formRow}>
                      <div className="w-full">
                        <label className={myStyles.formLabel} htmlFor="startDate">Start date</label>
                        <span className={myStyles.formFieldRequired}>*</span>
                        <Calendar
                          id="startDate"
                          onChange={formik.handleChange}
                          value={formik.values.startDate}
                          className={
                            classNames(
                              myStyles.formField,
                              getFieldValidation("startDate") ? myStyles.formFieldBorderError : myStyles.formFieldBorder,
                            )}
                          disabled={formik.isSubmitting}
                        />
                        <ValidationText fieldName="startDate" />
                      </div>

                      <div className="w-full">
                        <label className={myStyles.formLabel} htmlFor="startTime">End date</label>
                        <span className={myStyles.formFieldRequired}>*</span>
                        <Calendar
                          id="endDate"
                          onChange={formik.handleChange}
                          value={formik.values.endDate}
                          className={classNames(
                            myStyles.formField,
                            getFieldValidation("endDate") ? myStyles.formFieldBorderError : myStyles.formFieldBorder,
                          )}
                          disabled={formik.isSubmitting}
                        />
                        <ValidationText fieldName="endDate" />
                      </div>
                    </div>

                    <div className={myStyles.formRow}>
                      {decideTimeSelection()} {/* Render time selection for recurring appointment */}
                      {renderDurationInput()} {/* Render duration input */}
                    </div>

                    <hr className={myStyles.sectionDivider} />

                    <AppointmentsFormCron
                      cronValues={formik.values.cron}
                      startTime={formik.values.time}
                      onChangeCronValues={(cron) => {
                        formik.setFieldValue("cron", cron) // Set cron expression in formik.
                      }}
                      disabled={formik.isSubmitting}
                    />
                  </>
                )}

                <hr className={myStyles.sectionDivider} />

                <div className={myStyles.formRow}>
                  <div className="w-full">
                    <label className={myStyles.formLabel} htmlFor="startDate">Type</label>
                    <span className={myStyles.formFieldRequired}>*</span>
                    <Dropdown
                      id="type"
                      value={formik.values.type}
                      options={services} // Options for services.
                      onChange={formik.handleChange}
                      className={classNames(
                        myStyles.formField,
                        getFieldValidation("type") ? myStyles.formFieldBorderError : myStyles.formFieldBorder,
                      )}
                      // disabled={formik.values.isRequest}
                      disabled={formik.isSubmitting}
                    />
                    <ValidationText fieldName="type" />
                  </div>

                  <div className="w-full">
                    <label className={myStyles.formLabel} htmlFor="startDate">Channel</label>
                    <span className={myStyles.formFieldRequired}>*</span>
                    <Dropdown
                      id="channel"
                      onChange={formik.handleChange}
                      value={formik.values.channel}
                      options={engagementChannels} // Options for engagement channels.
                      className={classNames(
                        myStyles.formField,
                        getFieldValidation("channel") ? myStyles.formFieldBorderError : myStyles.formFieldBorder,
                        myStyles.capsText // Style to capitalize text (1st letter only).
                      )}
                      panelClassName={myStyles.capsText} // Style to capitalize text (1st letter only).
                      disabled={formik.isSubmitting}
                    />
                    <ValidationText fieldName="channel" />
                  </div>
                </div>
              </>
            )}

            <div className={myStyles.formContainerFooter}>
              <Button
                severity="primary"
                className={myStyles.roundedDialogButton}
                label='Submit'
                type="submit"
                loading={formik.isSubmitting} // Show loading state while submitting.
                disabled={
                  formik.isSubmitting || compareValues(initPayload, formik.values, ["organization", "staff", "id"]) // Ignore keys: organization, staff and id, as they are not editable.
                } // Disable button while submitting or if no changes have been made to the form.
              />
            </div>
          </div>
        )}
      </form>
    </Dialog>
  )
}
